import { Component, ChangeDetectionStrategy, inject, signal, computed, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  FormSelectDirective,
  InputGroupComponent,
  InputGroupTextDirective,
  RowComponent,
  SpinnerComponent,
  TableDirective
} from '@coreui/angular-pro';
import { TranslatePipe, TranslateService } from 'i18n';
import { ViacarteAdminService, ViacarteBalance, ViacarteCard, ViacarteCardSecureInfo, ViacarteTransaction, ViacarteUserDetail } from '../../../services/viacarte-admin.service';

@Component({
  selector: 'app-admin-viacarte-detail-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ButtonDirective,
    BadgeComponent,
    SpinnerComponent,
    TableDirective,
    FormControlDirective,
    FormSelectDirective,
    InputGroupComponent,
    InputGroupTextDirective,
    TranslatePipe
  ],
  templateUrl: './admin-viacarte-detail-page.component.html',
  styleUrls: ['./admin-viacarte-detail-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminViacarteDetailPageComponent {
  private service = inject(ViacarteAdminService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);

  userId = signal<number>(0);
  detail = signal<ViacarteUserDetail | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);

  // Pro Aktion ein eigener Busy-Marker, damit die Buttons gezielt sperren.
  busy = signal<string | null>(null);     // 'refresh' | 'provision' | `block:<id>` | `unblock:<id>`
  actionMessage = signal<{ type: 'success' | 'danger'; text: string } | null>(null);

  cards = computed<ViacarteCard[]>(() => {
    const c = this.detail()?.cards;
    return Array.isArray(c) ? c : [];
  });
  cardsError = computed<string | null>(() => {
    const c = this.detail()?.cards;
    return c && !Array.isArray(c) ? (c as any).error : null;
  });
  transactions = computed<ViacarteTransaction[]>(() => this.detail()?.transactions ?? []);

  // Client-seitiges Paging der Transaktionsliste.
  readonly txPageSizes = [10, 20, 50, 100];
  txPage = signal(1);
  txPageSize = signal(10);
  txTotalPages = computed(() => Math.max(1, Math.ceil(this.transactions().length / this.txPageSize())));
  pagedTransactions = computed<ViacarteTransaction[]>(() => {
    const size = this.txPageSize();
    const page = Math.min(this.txPage(), this.txTotalPages());
    const start = (page - 1) * size;
    return this.transactions().slice(start, start + size);
  });

  setTxPageSize(size: number): void { this.txPageSize.set(Number(size)); this.txPage.set(1); }
  txGoTo(p: number): void { if (p >= 1 && p <= this.txTotalPages()) this.txPage.set(p); }

  // Sandbox: Test-Transaktion simulieren.
  simAmount = signal<number | null>(null);
  simMerchant = signal<string>('Test Shop');
  simPost = signal(true); // sofort buchen (settled) statt pending

  simulate(cardId: string): void {
    if (!cardId) return;
    const amt = Number(this.simAmount());
    if (!amt || amt <= 0) { this.actionMessage.set({ type: 'danger', text: this.t('viacarte_admin.amount_invalid') }); return; }
    this.runAction(`sim:${cardId}`, this.service.simulateTransaction(this.userId(), cardId, amt, this.simMerchant().trim() || undefined, this.simPost()),
      'viacarte_admin.msg_simulated', () => this.simAmount.set(null));
  }

  // Sandbox: eine pending Transaktion buchen/bestätigen.
  postTransaction(t: ViacarteTransaction): void {
    if (!t.internal_transaction_id) return;
    this.runAction(`post:${t.internal_transaction_id}`,
      this.service.postTransaction(this.userId(), t.internal_transaction_id), 'viacarte_admin.msg_posted');
  }

  // ViaCarte-Systeme: KYC ist user-weit, Account + Karten sind je System getrennt.
  readonly SYSTEMS = ['fac3less', 'globalhealth', 'mnlo'];
  selectedSystem = signal<string>('fac3less');

  accounts = computed<Record<string, string | null>>(() => this.detail()?.accounts ?? {});
  accountBalances = computed<Record<string, ViacarteBalance | null>>(() => this.detail()?.account_balances ?? {});
  // Alle Systeme inkl. ihres (evtl. leeren) Kontos – für Auswahl + Stammdaten.
  systemList = computed(() => this.SYSTEMS.map(s => ({ system: s, accountId: this.accounts()[s] || null })));
  selectedAccountId = computed<string | null>(() => this.accounts()[this.selectedSystem()] || null);

  // Kontostand (oder Fehlerobjekt) des gewählten Systems.
  accountBalance = computed<ViacarteBalance | null>(() =>
    this.accountBalances()[this.selectedSystem()] ?? this.detail()?.account_balance ?? null);
  accountBalanceError = computed<string | null>(() => {
    const b = this.accountBalance();
    return b && b.error ? b.error : null;
  });

  // Vorbedingungen für Aktionen.
  canProvision = computed(() => this.detail()?.status === 'COMPLETED' && !!this.detail()?.customer_id);

  // Transfer (Aufladen / Zurückbuchen) auf dem gewählten System.
  fundAmount = signal<number | null>(null);
  hasAccount = computed(() => !!this.selectedAccountId());
  accountCurrency = computed<string>(() => this.accountBalance()?.balanceCurrency || '');

  // Karte -> Karte (z.B. physisch -> virtuell).
  cardFrom = signal<string>('');
  cardTo = signal<string>('');
  cardTransferAmount = signal<number | null>(null);

  // Sensible Kartendaten (PAN/CVV) – nur transient im Speicher, je Karte on-demand.
  secureInfo = signal<Record<string, ViacarteCardSecureInfo>>({});

  constructor() {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.userId.set(id);
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service.get(this.userId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (d) => { this.loading.set(false); this.detail.set(d); this.setCardDefaults(); this.secureInfo.set({}); },
        error: () => { this.loading.set(false); this.error.set('Failed to load detail'); }
      });
  }

  refresh(): void {
    this.runAction('refresh', this.service.refresh(this.userId()), 'viacarte_admin.msg_refreshed');
  }

  provision(): void {
    if (!confirm(this.t('viacarte_admin.confirm_provision'))) return;
    this.runAction('provision', this.service.provision(this.userId(), undefined, this.selectedSystem()), 'viacarte_admin.msg_provisioned');
  }

  fund(): void {
    const amt = Number(this.fundAmount());
    if (!amt || amt <= 0) { this.actionMessage.set({ type: 'danger', text: this.t('viacarte_admin.amount_invalid') }); return; }
    if (!confirm(this.t('viacarte_admin.confirm_fund'))) return;
    this.runAction('fund', this.service.fund(this.userId(), amt, undefined, this.selectedSystem()), 'viacarte_admin.msg_funded', () => this.fundAmount.set(null));
  }

  withdraw(): void {
    const amt = Number(this.fundAmount());
    if (!amt || amt <= 0) { this.actionMessage.set({ type: 'danger', text: this.t('viacarte_admin.amount_invalid') }); return; }
    if (!confirm(this.t('viacarte_admin.confirm_withdraw'))) return;
    this.runAction('withdraw', this.service.withdraw(this.userId(), amt, undefined, this.selectedSystem()), 'viacarte_admin.msg_withdrawn', () => this.fundAmount.set(null));
  }

  cardTransfer(): void {
    const amt = Number(this.cardTransferAmount());
    if (!amt || amt <= 0) { this.actionMessage.set({ type: 'danger', text: this.t('viacarte_admin.amount_invalid') }); return; }
    if (!this.cardFrom() || !this.cardTo()) { this.actionMessage.set({ type: 'danger', text: this.t('viacarte_admin.card_select_required') }); return; }
    if (this.cardFrom() === this.cardTo()) { this.actionMessage.set({ type: 'danger', text: this.t('viacarte_admin.card_same') }); return; }
    if (!confirm(this.t('viacarte_admin.confirm_card_transfer'))) return;
    this.runAction('card-transfer', this.service.cardTransfer(this.userId(), this.cardFrom(), this.cardTo(), amt),
      'viacarte_admin.msg_card_transferred', () => this.cardTransferAmount.set(null));
  }

  // Sinnvolle Vorauswahl: erstes System mit Konto (sonst Brand-System/fac3less)
  // sowie von physischer auf virtuelle Karte (nur wenn noch leer).
  private setCardDefaults(): void {
    const firstSystem = this.SYSTEMS.find(s => this.accounts()[s])
      || (this.detail()?.domain_id === 5 ? 'globalhealth' : 'fac3less');
    this.selectedSystem.set(firstSystem);

    const cs = this.cards();
    const phys = cs.find(c => c.cardTypeLabel === 'physical');
    const virt = cs.find(c => c.cardTypeLabel === 'virtual');
    if (phys && !this.cardFrom()) this.cardFrom.set(phys.id);
    if (virt && !this.cardTo()) this.cardTo.set(virt.id);
  }

  // PAN/CVV/Expiry on-demand laden (Toggle). Nicht persistiert.
  revealCard(card: ViacarteCard): void {
    if (this.secureInfo()[card.id]) { this.hideCard(card.id); return; }
    this.busy.set('secure:' + card.id);
    this.service.cardSecureInfo(this.userId(), card.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (d) => { this.busy.set(null); this.secureInfo.update(m => ({ ...m, [card.id]: d })); },
        error: (e) => {
          this.busy.set(null);
          this.secureInfo.update(m => ({ ...m, [card.id]: { error: e?.error?.error || this.t('viacarte_admin.msg_error') } }));
        }
      });
  }

  hideCard(cardId: string): void {
    this.secureInfo.update(m => { const n = { ...m }; delete n[cardId]; return n; });
  }

  activateCard(card: ViacarteCard): void {
    this.runAction(`activate:${card.id}`, this.service.activateCard(this.userId(), card.id), 'viacarte_admin.msg_activated');
  }

  deactivateCard(card: ViacarteCard): void {
    if (!confirm(this.t('viacarte_admin.confirm_deactivate'))) return;
    this.runAction(`deactivate:${card.id}`, this.service.deactivateCard(this.userId(), card.id), 'viacarte_admin.msg_deactivated');
  }

  blockCard(card: ViacarteCard): void {
    if (!confirm(this.t('viacarte_admin.confirm_block'))) return;
    this.runAction(`block:${card.id}`, this.service.blockCard(this.userId(), card.id), 'viacarte_admin.msg_blocked');
  }

  unblockCard(card: ViacarteCard): void {
    this.runAction(`unblock:${card.id}`, this.service.unblockCard(this.userId(), card.id), 'viacarte_admin.msg_unblocked');
  }

  private runAction(key: string, obs: ReturnType<ViacarteAdminService['refresh']>, successKey: string, onSuccess?: () => void): void {
    this.busy.set(key);
    this.actionMessage.set(null);
    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (d) => {
        this.busy.set(null);
        this.detail.set(d);
        this.actionMessage.set({ type: 'success', text: this.t(successKey) });
        onSuccess?.();
      },
      error: (e) => {
        this.busy.set(null);
        const msg = e?.error?.error || this.t('viacarte_admin.msg_error');
        this.actionMessage.set({ type: 'danger', text: msg });
      }
    });
  }

  back(): void {
    const lang = localStorage.getItem('language') || 'en';
    this.router.navigateByUrl(`/${lang}/admin/viacarte`);
  }

  // Übersetzungs-Helfer für confirm()/Meldungen (TranslatePipe ist nur im Template
  // verfügbar). Fällt auf den Key zurück, falls keine Übersetzung gefunden wird.
  private t(key: string): string {
    return this.translate.translate(key) || key;
  }

  // Pre-Registration als Liste von Schlüssel/Wert-Paaren (für die Detailtabelle).
  preregEntries(): { key: string; value: string }[] {
    const p = this.detail()?.preregistration;
    if (!p || typeof p !== 'object' || p.error) return [];
    return Object.keys(p).map(k => ({ key: k, value: this.stringify(p[k]) }));
  }

  preregError(): string | null {
    const p = this.detail()?.preregistration;
    return p && p.error ? p.error : null;
  }

  stringify(v: any): string {
    if (v == null) return '—';
    if (typeof v === 'object') { try { return JSON.stringify(v); } catch { return String(v); } }
    return String(v);
  }

  formatDate(d: string | null | undefined): string {
    if (!d) return '—';
    return new Date(d).toLocaleString();
  }

  // Formatiert einen Betrag mit Währung (z.B. "12.50 USD"). null -> "—".
  formatAmount(amount: number | undefined | null, currency?: string): string {
    if (amount == null) return '—';
    return `${Number(amount).toFixed(2)} ${currency || ''}`.trim();
  }

  // Kompakter verfügbarer Saldo einer Karte (für die Tabellenspalte).
  cardAvailable(card: ViacarteCard): string {
    const b = card.balance;
    if (!b || b.error) return '—';
    return this.formatAmount(b.availableBalanceAmount, b.balanceCurrency);
  }

  cardTypeBadge(label?: string): string {
    if (label === 'physical') return 'success';
    if (label === 'virtual') return 'primary';
    return 'secondary';
  }

  // ViaCarte liefert den Kartenstatus als interner Integer `statusNetwork`; das
  // Code→Klartext-Mapping ist NICHT dokumentiert. Bekannte Codes hier eintragen,
  // sobald von ViaCarte/Greyner bestätigt; unbekannte Codes werden als Rohwert
  // angezeigt – wir behaupten also nie fälschlich "Aktiv".
  private static readonly CARD_STATUS: Record<number, { key: string; color: string }> = {
    1000: { key: 'viacarte_admin.active',        color: 'success' },   // aktiv/nutzbar (bestätigt: virtuelle Karte)
    1005: { key: 'viacarte_admin.not_activated', color: 'warning' },   // ausgegeben, noch nicht aktiviert (bestätigt: physische Karte)
    1007: { key: 'viacarte_admin.inactive',      color: 'secondary' }, // vom User selbst auf inaktiv gesetzt (bestätigt)
  };

  cardStatusBadge(c: ViacarteCard): string {
    if (c.isBlocked === true) return 'danger';
    const m = c.statusNetwork != null ? AdminViacarteDetailPageComponent.CARD_STATUS[c.statusNetwork] : undefined;
    return m?.color ?? 'secondary';
  }

  cardStatusLabel(c: ViacarteCard): string {
    if (c.isBlocked === true) return this.t('viacarte_admin.blocked');
    if (c.statusNetwork != null) {
      const m = AdminViacarteDetailPageComponent.CARD_STATUS[c.statusNetwork];
      return m ? this.t(m.key) : `${this.t('viacarte_admin.status_code')} ${c.statusNetwork}`;
    }
    return this.t('viacarte_admin.unknown_status');
  }
}
